/**
 * Per-screen context for the floating onboarding agent. Maps the current route to
 * a short description of what the screen is for and how to use it, so the voice
 * agent can explain exactly where the user is and guide them from here.
 */

import { RELEASE_NOTES } from "@scoach/types";

export interface ScreenContext {
  key: string;
  title: string;
  /** Plain-language description of the screen and its key actions. */
  description: string;
}

/** What SellSynq is, app-wide — always included so the agent can answer "what is this?". */
const APP_OVERVIEW = `SellSynq is an AI real-time meeting assistant for Google Cloud sellers and Customer Engineers. During a live meeting it transcribes the conversation, surfaces coaching hints and follow-up questions, answers questions on demand ("Quiet ask"), can draw architecture/decision diagrams and infographics, and afterwards produces a structured summary with action items. Users can also run practice simulations, manage clients, track tasks, do self-training, and (for admins) review team activity.`;

/** The most recent release notes, so the agent can tell users what's new in SellSynq. */
function whatsNewBlock(): string {
  const recent = RELEASE_NOTES.slice(0, 3)
    .map((n) => `• v${n.version} (${n.date}) — ${n.title}: ${n.items.slice(0, 5).join("; ")}`)
    .join("\n");
  return `WHAT'S NEW IN SELLSYNQ (most recent first):\n${recent}`;
}

/** Route → screen context. Matched by longest specific rule first. */
function matchScreen(pathname: string): ScreenContext {
  const p = pathname;

  if (/\/meetings\/[^/]+\/live/.test(p)) {
    return {
      key: "meeting-live",
      title: "Live meeting",
      description:
        "This is the live meeting screen. The left rail controls listening (mic / share tab audio) and language. The centre shows coaching hints and follow-up questions in real time. The right panel has tabs: Infographic, Diagram / Decision tree (for technical meetings and CEs), What's New, Use Cases, Transcript, and Captions (live translated captions — pick a language). At the bottom is Quiet ask, where the rep types or dictates a question and gets a private answer. To begin capturing, enable listening on the left rail. To stop, use End meeting, which generates the summary.",
    };
  }
  if (/\/meetings\/[^/]+\/setup/.test(p)) {
    return {
      key: "meeting-setup",
      title: "Meeting setup",
      description:
        "This is meeting setup. Here the user picks the company/client, title, stage, goal, per-meeting role, spoken and assistance languages, and the Meeting type (Standard, Technical — which adds live architecture diagrams — or Training/learning — which produces a much longer, explanatory summary you can learn from). They can attach context documents. 'Start meeting' opens the live screen; 'Schedule' saves it for later.",
    };
  }
  if (/\/meetings\/[^/]+\/summary/.test(p)) {
    return {
      key: "meeting-summary",
      title: "Meeting summary",
      description:
        "This is the post-meeting summary: an overview, what went well, where to push deeper, stated vs actual needs, action items, risks, upsell, top moments, and any diagrams. Users can talk with the meeting (chat over the transcript), view the transcript, export to Google Drive, and share the summary.",
    };
  }
  if (p.startsWith("/dashboard")) {
    return {
      key: "dashboard",
      title: "Dashboard",
      description:
        "This is the dashboard — the home hub. Two buttons start work: '🔍 Discovery' (deep-research a company before a meeting) and '⚡ Quick Meeting' (go straight into a live meeting). It also shows recent/scheduled meetings and upcoming Google Calendar meetings (each has its own 'Start meeting'), plus this-week stats. To coach a call now, use 'Quick Meeting'. To prep a company first, use 'Discovery'.",
    };
  }
  if (p.startsWith("/clients")) {
    return {
      key: "clients",
      title: "Clients",
      description:
        "This is the Clients page — the companies the user meets with. They can open a client to see its meetings and tasks, rename, or hide a client. Company research and reports are attached per client.",
    };
  }
  if (p.startsWith("/training")) {
    return {
      key: "training",
      title: "My ongoing training",
      description:
        "This is the training area. It builds study materials — from the user's own meetings or from their role's latest products — as a podcast, slides, quiz, and infographic, with grounded video/article resources. Users pick a mode, generate materials, and mark them done.",
    };
  }
  if (p.startsWith("/simulations")) {
    return {
      key: "simulations",
      title: "Simulations",
      description:
        "This is Simulations — AI role-play practice calls. The user picks a scenario and difficulty and runs a mock meeting where SellSynq plays the customer, then gets a scored summary.",
    };
  }
  if (p.startsWith("/tasks")) {
    return {
      key: "tasks",
      title: "Tasks",
      description:
        "This is Tasks — the follow-up action items pulled from meeting summaries, so the user can track and complete them.",
    };
  }
  if (p.startsWith("/discovery")) {
    return {
      key: "discovery",
      title: "Discovery",
      description:
        "This is Discovery — a quick way to start a meeting for a returning customer, pre-filling their context, or to search prior context.",
    };
  }
  if (p.startsWith("/company")) {
    return {
      key: "company",
      title: "Company Hub",
      description:
        "This is Company Hub — manage company positioning, value proposition, products and services, and upload documents to ground live AI coaching and battlecards.",
    };
  }
  if (p.startsWith("/developers")) {
    return {
      key: "developers",
      title: "Developer platform",
      description:
        "This is the Developer platform — docs, a playground, and management for building in-app plugins and API tokens.",
    };
  }
  if (p.startsWith("/settings")) {
    return {
      key: "settings",
      title: "Settings",
      description:
        "This is Settings, with tabs: Profile (name, role, team), Key (Gemini API key), Language, Coaching (hint strategy, assistance language, custom questions, and the onboarding-agent toggle), and Summary (Drive export folder, custom summary). Changes are saved with the Save button.",
    };
  }
  if (p.startsWith("/admin") || p.startsWith("/interview-admin")) {
    return {
      key: "admin",
      title: "Admin",
      description:
        "This is the Admin area — team dashboards, meeting drill-downs, reports (Voice of the Field, product feedback, user bugs), user approval/blocking, and content management.",
    };
  }

  return {
    key: "app",
    title: "SellSynq",
    description:
      "The user is somewhere in the SellSynq app. Offer to explain the main areas: Dashboard (start meetings), Clients, Simulations, Training, Tasks, Company Hub, and Settings.",
  };
}

export function screenContextFor(pathname: string): ScreenContext {
  return matchScreen(pathname);
}

export interface OnboardingPromptOpts {
  /** The user's role label (e.g. "Account Executive", "CE AI"). */
  roleLabel?: string;
  /** True for technical (Customer-Engineer-type) roles → teach at architecture depth. */
  technical?: boolean;
}

/** Build the Gemini Live system instruction for the onboarding + learning agent. */
export function buildOnboardingSystemPrompt(
  ctx: ScreenContext,
  opts?: OnboardingPromptOpts,
): string {
  const roleLine = opts?.roleLabel ? `The user's role is: ${opts.roleLabel}.` : "";
  const depthLine = opts?.technical
    ? "This user is TECHNICAL (a solutions architect / sales engineer). When teaching, go to real depth: architecture, trade-offs, limits, and hands-on integration steps."
    : "This user is a SALES/business role (not a deep engineer). When teaching, stay higher-level and outcome-focused: customer value, ROI, when to use it, and simple analogies — avoid deep implementation detail unless asked.";

  return `You are SellSynq's guide and coach — a friendly voice assistant with FOUR jobs, not just onboarding:
1) ONBOARDING — explain how to use the SellSynq app and guide the user around it.
2) COACHING — using the user's recent meetings and coaching insights (provided to you in a "[YOUR MEETINGS & COACHING CONTEXT]" message), suggest what they should learn or improve next, and why.
3) PRIVATE TEACHER — actually teach a topic conversationally, like a personal tutor. If they say "explain RAG" or "how does X work", walk them through it step by step, check understanding, and offer to go deeper or point them to "My training" to generate study materials (podcast/slides/quiz).
4) WHAT'S NEW — tell them what's new in SellSynq and offer to open the What's-new popup.

Speak naturally and concisely. ${roleLine} ${depthLine}

WHEN THE SESSION STARTS: greet the user in ONE short sentence and offer the menu — e.g. "I can (1) show you around SellSynq, (2) look at your recent meetings and suggest what to get better at, (3) teach you a topic like a private tutor, or (4) tell you what's new. What would you like?" Then wait for their choice. Keep it brief and warm.

When COACHING: ground suggestions in the provided meetings/insights — name the specific gap and a concrete topic to learn (e.g. "in your last two discovery calls you didn't cover pricing — want me to teach you pricing defense and objection handling?"). Offer to teach it right now or to open "My training" to make materials.

${APP_OVERVIEW}

${whatsNewBlock()}

The user is currently on: ${ctx.title}.
${ctx.description}

CRITICAL — be accurate, never make things up:
- You are fed live "[SCREEN UPDATE — current view]" messages that reflect what the user sees right now, INCLUDING any popup/dialog that opens. Always trust the most recent one. If it says a popup/dialog is open, the user is looking at THAT — talk about it.
- The static description above may be out of date, and the user may have scrolled or be pointing at something. BEFORE you give directions, name a button, say "where/how do I…", or highlight anything, CALL the "readScreen" tool for the freshest view (current screen, visible buttons/links/tabs, any open popup, and what the user is hovering). Base your answer ONLY on that plus the app knowledge above.
- If the user is hovering a control, assume they're asking about THAT — explain what it does.
- Only reference screens, buttons, and features that readScreen or this prompt actually shows. If something isn't there, say you don't see it rather than guessing. Never invent buttons, menu items, meeting types, or flows.
- To guide them: call "navigate" to move to another screen (dashboard, clients, training, simulations, tasks, discovery, marketplace, developers, settings). When the user is on the live meeting screen, you can switch between tabs by calling "switchMeetingTab" (or "navigate") with: infographic, diagram, decisiontree, whatsnew, usecase, transcript, or captions. Call "highlight" with the EXACT visible label from readScreen to point at a control. Do one small step at a time, then say what you did and what's next.
- WHAT'S NEW: You know the latest SellSynq updates (above). When it fits — e.g. on the first exchange, or if the user asks what's new / what changed — briefly mention the newest highlights and offer to open the full "What's new" list. If they say yes, call the "showWhatsNew" tool to pop it open. Don't over-push it.
- Keep it to SellSynq. If asked something unrelated, steer back. If you're unsure, say so and offer to look at the screen.`;
}

/** Whitelisted navigation targets the agent may jump to. */
export const NAV_TARGETS: Record<string, string> = {
  dashboard: "/dashboard",
  clients: "/clients",
  training: "/training",
  simulations: "/simulations",
  tasks: "/tasks",
  discovery: "/discovery",
  marketplace: "/marketplace",
  developers: "/developers",
  settings: "/settings",
};
